import { UnstyledButton } from '@mantine/core';
import {
  IconAlertOctagon,
  IconCalendarClock,
  IconClipboardCheck,
  IconRefreshDot,
  IconTool,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { decisionKind } from './OfficeDecisionDrawer.js';
import work from './PmsWork.module.css';
import type { PendingItem } from './types.js';

/**
 * Zil maddesinin ekranı (plan M7 Task 14a, 14b): iş kaydı onayı iş emri detayında, erteleme ve
 * aralık değişikliği kararı Maintenance Plan'ın karar çekmecesinde (`?decide=`); ofiste gemi
 * seçili açılır (`?vessel=`).
 */
export function pmsBellPath(
  item: Pick<PendingItem, 'recordType' | 'recordId' | 'vesselId' | 'workOrderId'>,
  scope: 'web' | 'ship',
): string {
  const vessel = scope === 'web' ? `vessel=${encodeURIComponent(item.vesselId)}&` : '';
  const kind = decisionKind(item);
  // Arıza ve breakdown kapanışı (M7c): Work Orders'ın sayfa içi sekmesinde (ADR 0027 §12)
  if (kind === 'defect' || kind === 'breakdown') {
    const tab = kind === 'breakdown' ? 'breakdowns' : 'defects';
    const open = scope === 'web' ? 'decide' : 'defect';
    return `/pms/work-orders?${vessel}tab=${tab}&${open}=${encodeURIComponent(item.recordId)}`;
  }
  if (kind === 'record' && item.workOrderId) {
    return `/pms/work-orders?${vessel}wo=${encodeURIComponent(item.workOrderId)}`;
  }
  return `/pms/plan?${vessel}decide=${encodeURIComponent(item.recordId)}`;
}

const ICONS = {
  record: <IconClipboardCheck size={18} />,
  postponement: <IconCalendarClock size={18} />,
  interval: <IconRefreshDot size={18} />,
  defect: <IconTool size={18} />,
  breakdown: <IconAlertOctagon size={18} />,
} as const;

/** Zilde kişinin karar vereceği PMS kayıtları; her biri sayılır (K8, K10, K11). */
export function PmsBellList({
  items,
  onOpen,
}: {
  items: readonly PendingItem[] | null;
  onOpen: (item: PendingItem) => void;
}) {
  const { t } = useTranslation();
  if (!items || items.length === 0) return null;
  return (
    <ul className={work.bellList} aria-label={t('pms.work.bell.title')}>
      {items.map((item) => {
        const kind = decisionKind(item);
        return (
          <li key={item.recordId}>
            <UnstyledButton className={work.bellItem} onClick={() => onOpen(item)}>
              <span className={work.bellIcon} aria-hidden>
                {ICONS[kind]}
              </span>
              <span>
                <strong>{t(`pms.work.bell.${kind}`)}</strong>
                <span className={work.note}>
                  {[item.defectNo ?? item.jobCode, item.equipmentName].filter(Boolean).join(' · ')}
                </span>
              </span>
            </UnstyledButton>
          </li>
        );
      })}
    </ul>
  );
}
